import { supabase } from '../lib/supabase';

export const twilioService = {
  // Send SMS notification
  async sendSMS(phoneNumber, message) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-sms-notification', {
        body: {
          to: phoneNumber,
          message: message
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error sending SMS:', error);
      return { data: null, error: error?.message || 'Failed to send SMS' };
    }
  },

  // Get user's SMS preferences
  async getSMSPreferences(userId) {
    try {
      const { data, error } = await supabase
        ?.from('sms_notification_preferences')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching SMS preferences:', error);
      return { data: null, error: error?.message || 'Failed to fetch SMS preferences' };
    }
  },

  // Update SMS preferences
  async updateSMSPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        ?.from('sms_notification_preferences')
        ?.update(preferences)
        ?.eq('user_id', userId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating SMS preferences:', error);
      return { data: null, error: error?.message || 'Failed to update SMS preferences' };
    }
  },

  // Update user phone number
  async updatePhoneNumber(userId, phoneNumber, verified = false) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          phone_number: phoneNumber,
          phone_verified: verified,
          sms_notifications_enabled: verified
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating phone number:', error);
      return { data: null, error: error?.message || 'Failed to update phone number' };
    }
  },

  // Log SMS notification
  async logNotification(logData) {
    try {
      const { data, error } = await supabase
        ?.from('sms_notification_logs')
        ?.insert([logData])
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error logging notification:', error);
      return { data: null, error: error?.message || 'Failed to log notification' };
    }
  },

  // Get notification logs for user
  async getNotificationLogs(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('sms_notification_logs')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching notification logs:', error);
      return { data: [], error: error?.message || 'Failed to fetch notification logs' };
    }
  },

  // Send marketplace notification
  async sendMarketplaceNotification(userId, notificationType, metadata = {}) {
    try {
      // Get user profile with phone number
      const { data: userProfile, error: userError } = await supabase
        ?.from('user_profiles')
        ?.select('phone_number, phone_verified, sms_notifications_enabled')
        ?.eq('id', userId)
        ?.single();

      if (userError) throw userError;

      // Check if user has SMS enabled
      if (!userProfile?.phone_verified || !userProfile?.sms_notifications_enabled) {
        return { data: null, error: 'SMS notifications not enabled for user' };
      }

      // Check if user wants this type of notification
      const { data: preferences, error: prefError } = await this.getSMSPreferences(userId);
      if (prefError) throw prefError;

      const shouldSend = this.shouldSendNotification(notificationType, preferences);
      if (!shouldSend) {
        return { data: null, error: 'User has disabled this notification type' };
      }

      // Generate message based on notification type
      const message = this.generateNotificationMessage(notificationType, metadata);

      // Send SMS
      const { data: smsData, error: smsError } = await this.sendSMS(
        userProfile?.phone_number,
        message
      );

      if (smsError) throw smsError;

      // Log notification
      await this.logNotification({
        user_id: userId,
        notification_type: notificationType,
        phone_number: userProfile?.phone_number,
        message: message,
        status: 'sent',
        twilio_message_sid: smsData?.messageSid,
        metadata: metadata,
        sent_at: new Date()?.toISOString()
      });

      return { data: smsData, error: null };
    } catch (error) {
      console.error('Error sending marketplace notification:', error);
      
      // Log failed notification
      await this.logNotification({
        user_id: userId,
        notification_type: notificationType,
        phone_number: '',
        message: '',
        status: 'failed',
        error_message: error?.message,
        metadata: metadata
      });

      return { data: null, error: error?.message || 'Failed to send notification' };
    }
  },

  // Check if notification should be sent based on preferences
  shouldSendNotification(notificationType, preferences) {
    if (!preferences) return false;

    const typeMapping = {
      offer_received: 'offer_alerts',
      offer_accepted: 'offer_alerts',
      offer_rejected: 'offer_alerts',
      offer_countered: 'offer_alerts',
      escrow_payment_received: 'escrow_alerts',
      escrow_funds_released: 'escrow_alerts',
      dispute_opened: 'dispute_alerts',
      dispute_resolved: 'dispute_alerts',
      listing_sold: 'transaction_alerts',
      transaction_completed: 'transaction_alerts'
    };

    const preferenceKey = typeMapping?.[notificationType];
    return preferences?.[preferenceKey] !== false;
  },

  // Generate notification message
  generateNotificationMessage(notificationType, metadata) {
    const messages = {
      offer_received: `New offer received: $${metadata?.amount} for "${metadata?.listingTitle}". Check your marketplace dashboard.`,
      offer_accepted: `Your offer of $${metadata?.amount} for "${metadata?.listingTitle}" was accepted! Proceed to payment.`,
      offer_rejected: `Your offer for "${metadata?.listingTitle}" was declined. You can make a new offer.`,
      offer_countered: `Counter offer: $${metadata?.counterAmount} for "${metadata?.listingTitle}". Review and respond.`,
      escrow_payment_received: `Payment of $${metadata?.amount} received in escrow for "${metadata?.listingTitle}". Awaiting confirmation.`,
      escrow_funds_released: `Escrow funds of $${metadata?.amount} released for "${metadata?.listingTitle}". Transaction complete!`,
      dispute_opened: `Dispute opened for transaction "${metadata?.listingTitle}". Please review and respond.`,
      dispute_resolved: `Dispute resolved for "${metadata?.listingTitle}". Check resolution details.`,
      listing_sold: `Your listing "${metadata?.listingTitle}" sold for $${metadata?.amount}! Funds in escrow.`,
      transaction_completed: `Transaction completed for "${metadata?.listingTitle}". Rate your experience.`
    };

    return messages?.[notificationType] || 'You have a new marketplace notification.';
  }
};